/**
 * look-sections.js
 * Premium bundle PDP — all section interactivity
 * Compatible with Shopify Impulse theme / OS 2.0
 *
 * KEY ARCHITECTURE:
 *   The Shopify Bundles app creates the bundle as a standard Shopify product.
 *   Component variants are combined into the bundle product's own options/variants.
 *   e.g. "Shirt Size" (S/M/L) + "Trouser Size" (28/30/32) = 9 bundle variants.
 *
 *   This JS:
 *   1. Reads window.__lookBundleProduct.variants (inlined by Liquid)
 *   2. When user changes any option button/select, finds the matching bundle variant
 *   3. Updates the hidden #id input in the buy-box form
 *   4. Updates price display and ATC button availability
 *
 * Responsibilities:
 *   1. Smooth scroll for Hero CTAs
 *   2. Bundle option selection → variant ID sync → buy-box form update
 *   3. Price update when variant changes
 *   4. Gallery lightbox
 *   5. Product recommendations lazy-load
 *   6. ATC loading state
 */

(function () {
  'use strict';

  const qs  = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* -------------------------------------------------------------------------
     1. Hero Slideshow — auto-scrolls through images, dot indicators
     ----------------------------------------------------------------------- */
  function initHeroSlideshow() {
    const track = qs('[data-hero-slides]');
    if (!track) return;

    const slides   = qsa('[data-slide]', track);
    const dotsWrap = qs('[data-hero-dots]');
    const dots     = dotsWrap ? qsa('[data-hero-dot]', dotsWrap) : [];
    const delay    = Number(track.dataset.autoplay) || 4000;
    const total    = slides.length;
    if (total <= 1) return;

    let current = 0;
    let timer   = null;

    function goTo(idx) {
      slides[current].classList.remove('is-active');
      slides[current].setAttribute('aria-hidden', 'true');
      if (dots[current]) {
        dots[current].classList.remove('is-active');
        dots[current].setAttribute('aria-current', 'false');
      }
      current = (idx + total) % total;
      slides[current].classList.add('is-active');
      slides[current].setAttribute('aria-hidden', 'false');
      if (dots[current]) {
        dots[current].classList.add('is-active');
        dots[current].setAttribute('aria-current', 'true');
      }
    }

    function start()  { timer = setInterval(() => goTo(current + 1), delay); }
    function stop()   { clearInterval(timer); }
    function reset()  { stop(); start(); }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => { goTo(Number(dot.dataset.heroDot)); reset(); });
    });

    let tx = 0;
    track.addEventListener('touchstart', (e) => { tx = e.touches[0].clientX; }, { passive: true });
    track.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - tx;
      if (Math.abs(dx) > 50) { goTo(dx < 0 ? current + 1 : current - 1); reset(); }
    }, { passive: true });

    track.addEventListener('mouseenter', stop);
    track.addEventListener('mouseleave', start);

    start();
  }

  /* -------------------------------------------------------------------------
     2. Smooth Scroll
     ----------------------------------------------------------------------- */
  function initSmoothScroll() {
    qsa('.js-scroll-to').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = document.getElementById(btn.dataset.target);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', '#' + btn.dataset.target);
      });
    });
  }

  /* -------------------------------------------------------------------------
     2. Bundle Option Selection
     ----------------------------------------------------------------------- */

  // Collect the currently selected value for each option position (1-based)
  function collectSelectedOptions() {
    const selected = {};

    // From buttons
    qsa('[data-option-btn].is-selected').forEach((btn) => {
      selected[btn.dataset.position] = btn.dataset.value;
    });

    // From selects (overrides if present)
    qsa('[data-option-select]').forEach((sel) => {
      selected[sel.dataset.position] = sel.value;
    });

    return selected;
  }

  // Find bundle product variant matching all currently selected options
  function findSelectedVariant() {
    const data = window.__lookBundleProduct;
    if (!data || !data.variants) return null;

    const selected = collectSelectedOptions();
    const positions = Object.keys(selected).sort();

    return data.variants.find((variant) => {
      return variant.options.every((optVal, idx) => {
        const position = String(idx + 1);
        return selected[position] === optVal;
      });
    }) || null;
  }

  // Push the matched variant ID into the buy-box hidden input + update UI
  function applyVariantSelection() {
    const variant      = findSelectedVariant();
    const variantInput = qs('[data-bundle-variant-input]');
    const addBtn       = qs('[data-add-to-cart]');
    const addBtnText   = qs('[data-add-to-cart-text]');
    const saleEl       = qs('[data-sale-price]');
    const compareEl    = qs('[data-compare-price]');
    const buyNowBtn    = qs('[data-buy-now]');

    if (variant) {
      if (variantInput) variantInput.value = variant.id;

      const available = variant.available;
      if (addBtn) {
        addBtn.disabled = !available;
        if (addBtnText) addBtnText.textContent = available ? 'Add This Look to Cart' : 'Sold Out';
      }
      if (buyNowBtn) buyNowBtn.disabled = !available;

      if (saleEl) saleEl.textContent = formatMoney(variant.price);

      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
    } else {
      if (addBtn) addBtn.disabled = true;
      if (addBtnText) addBtnText.textContent = 'Unavailable';
      if (buyNowBtn) buyNowBtn.disabled = true;
    }
  }

  // Money formatter — reads Shopify money format from page meta or falls back
  function formatMoney(cents) {
    // Try Shopify's built-in meta tag first
    const fmt = window.Shopify?.money_format || '₹{{amount}}';
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  // Buy Now — submit form then redirect to checkout
  function initBuyNow() {
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-buy-now]')) return;
      const form = document.getElementById('bundle-product-form');
      if (!form) return;

      // Clone form, add return_to=checkout, submit
      const clone = form.cloneNode(true);
      clone.style.display = 'none';
      const returnInput = document.createElement('input');
      returnInput.type  = 'hidden';
      returnInput.name  = 'return_to';
      returnInput.value = '/checkout';
      clone.appendChild(returnInput);
      document.body.appendChild(clone);
      clone.submit();
    });
  }

  // Handle option button click
  function handleOptionBtnClick(e) {
    const btn = e.currentTarget;
    if (btn.classList.contains('is-unavailable')) return;

    const position = btn.dataset.position;

    // Deselect all sibling buttons for this option position
    qsa(`[data-option-btn][data-position="${position}"]`).forEach((b) => {
      b.classList.remove('is-selected');
      b.setAttribute('aria-pressed', 'false');
    });

    // Select clicked button
    btn.classList.add('is-selected');
    btn.setAttribute('aria-pressed', 'true');

    // Update the label showing selected value
    const label = qs(`[data-selected-label="${position}"]`);
    if (label) label.textContent = btn.dataset.value;

    applyVariantSelection();
    updateUnavailableStates();
  }

  // Handle select dropdown change
  function handleOptionSelectChange(e) {
    const sel      = e.currentTarget;
    const position = sel.dataset.position;
    const label    = qs(`[data-selected-label="${position}"]`);
    if (label) label.textContent = sel.value;

    applyVariantSelection();
    updateUnavailableStates();
  }

  // Grey out option buttons that lead to no available variant
  function updateUnavailableStates() {
    const data = window.__lookBundleProduct;
    if (!data || !data.variants) return;

    const selected = collectSelectedOptions();

    qsa('[data-option-btn]').forEach((btn) => {
      const position = btn.dataset.position;
      const value    = btn.dataset.value;

      // Build a hypothetical selection with this button chosen
      const hypothetical = { ...selected, [position]: value };
      const positions    = Object.keys(hypothetical).sort();

      const hasAvailable = data.variants.some((variant) => {
        return variant.available && variant.options.every((optVal, idx) => {
          const pos = String(idx + 1);
          return hypothetical[pos] === undefined || hypothetical[pos] === optVal;
        });
      });

      btn.classList.toggle('is-unavailable', !hasAvailable);
      if (!hasAvailable) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    });
  }

  function initBundleOptionSelectors() {
    qsa('[data-option-btn]').forEach((btn) => {
      btn.addEventListener('click', handleOptionBtnClick);
    });

    qsa('[data-option-select]').forEach((sel) => {
      sel.addEventListener('change', handleOptionSelectChange);
    });

    // Run once on load to initialise state
    applyVariantSelection();
    updateUnavailableStates();
  }

  /* -------------------------------------------------------------------------
     3. Add to Cart — Loading State
     ----------------------------------------------------------------------- */
  function initAddToCartForm() {
    const form = document.getElementById('bundle-product-form');
    if (!form) return;

    form.addEventListener('submit', () => {
      const btn        = qs('[data-add-to-cart]', form);
      const spinnerEl  = qs('.buy-box-bundle__spinner', form);
      const textEl     = qs('[data-add-to-cart-text]', form);

      if (btn) btn.disabled = true;
      if (textEl) textEl.style.display = 'none';

      if (spinnerEl) {
        // Inject SVG only at submit time — never present in DOM before then
        spinnerEl.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2.5" stroke-dasharray="32" stroke-dashoffset="10"/></svg>';
        spinnerEl.style.display = 'flex';
      }
    });
  }

  /* -------------------------------------------------------------------------
     4. Gallery Lightbox — supports View More overlay + thumbnail strip
     ----------------------------------------------------------------------- */
  function initGalleryLightbox() {
    const lightbox  = qs('[data-gallery-lightbox]');
    if (!lightbox) return;

    const slides    = qsa('[data-slide]', lightbox);
    const thumbs    = qsa('[data-gallery-thumb]', lightbox);
    const closeBtn  = qs('[data-gallery-close]', lightbox);
    const prevBtn   = qs('[data-gallery-prev]', lightbox);
    const nextBtn   = qs('[data-gallery-next]', lightbox);
    const counter   = qs('[data-gallery-counter]', lightbox);
    const total     = slides.length;
    let current     = 0;

    function showSlide(idx) {
      slides.forEach((s) => s.classList.remove('is-active'));
      thumbs.forEach((t) => t.classList.remove('is-active'));
      const slide = slides[idx];
      const thumb = thumbs[idx];
      if (slide) { slide.classList.add('is-active'); current = idx; }
      if (thumb) {
        thumb.classList.add('is-active');
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
      if (counter) counter.textContent = `${idx + 1} / ${total}`;
    }

    function open(idx) {
      lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
      showSlide(idx);
      lightbox.focus();
    }

    function close() {
      lightbox.hidden = true;
      document.body.style.overflow = '';
    }

    // Open on individual image click
    qsa('[data-gallery-open]').forEach((btn) => {
      btn.addEventListener('click', () => open(Number(btn.dataset.galleryOpen)));
    });

    // Open on "View All" overlay click (same button as last image)
    qsa('[data-gallery-view-more]').forEach((el) => {
      el.closest('button')?.addEventListener('click', (e) => {
        e.stopPropagation();
        open(0);
      });
    });

    closeBtn?.addEventListener('click', close);
    prevBtn?.addEventListener('click', () => showSlide((current - 1 + total) % total));
    nextBtn?.addEventListener('click', () => showSlide((current + 1) % total));

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => showSlide(Number(thumb.dataset.galleryThumb)));
    });

    lightbox.addEventListener('keydown', (e) => {
      if (e.key === 'Escape')     close();
      if (e.key === 'ArrowLeft')  showSlide((current - 1 + total) % total);
      if (e.key === 'ArrowRight') showSlide((current + 1) % total);
    });

    lightbox.setAttribute('tabindex', '-1');

    // Close on backdrop click
    qs('[data-gallery-stage]', lightbox)?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) close();
    });

    // Touch swipe
    let tx = 0;
    lightbox.addEventListener('touchstart', (e) => { tx = e.touches[0].clientX; }, { passive: true });
    lightbox.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - tx;
      if (Math.abs(dx) > 50) {
        dx < 0 ? showSlide((current + 1) % total) : showSlide((current - 1 + total) % total);
      }
    }, { passive: true });
  }

  /* -------------------------------------------------------------------------
     5. Fit & Fabric Accordions
     ----------------------------------------------------------------------- */
  function initAccordions() {
    qsa('[data-accordion]').forEach((accordion) => {
      const trigger  = qs('[data-accordion-trigger]', accordion);
      if (!trigger) return;
      const bodyId   = trigger.getAttribute('aria-controls');
      const body     = bodyId ? document.getElementById(bodyId) : null;

      trigger.addEventListener('click', () => {
        const isOpen = accordion.hasAttribute('data-open');
        if (isOpen) {
          accordion.removeAttribute('data-open');
          trigger.setAttribute('aria-expanded', 'false');
          if (body) body.hidden = true;
        } else {
          accordion.setAttribute('data-open', '');
          trigger.setAttribute('aria-expanded', 'true');
          if (body) body.hidden = false;
        }
      });
    });
  }

  /* -------------------------------------------------------------------------
     5. Product Recommendations
     ----------------------------------------------------------------------- */
  function initProductRecommendations() {
    const section = qs('[data-recommendations-url]');
    if (!section) return;

    fetch(section.dataset.recommendationsUrl)
      .then((r) => r.ok ? r.text() : Promise.reject(r.status))
      .then((html) => {
        const doc       = new DOMParser().parseFromString(html, 'text/html');
        const newGrid   = qs('.complete-look__grid', doc);
        const container = qs('[data-recommendations-container]', section);
        if (newGrid && container) {
          container.innerHTML = newGrid.innerHTML;
          container.classList.remove('complete-look__grid--loading');
        }
      })
      .catch((err) => console.warn('[look-sections] Recommendations failed:', err));
  }

  /* -------------------------------------------------------------------------
     Init
     ----------------------------------------------------------------------- */

  function init() {
    initHeroSlideshow();
    initSmoothScroll();
    initBundleOptionSelectors();
    initAddToCartForm();
    initBuyNow();
    initGalleryLightbox();
    initAccordions();
    initProductRecommendations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
